export class Button {
    private readonly button: HTMLButtonElement;
    constructor(selector: string) {
        this.button = document.querySelector<HTMLButtonElement>(selector)!;
    }
    onClick(callback: () => void) {
        this.button.addEventListener("click", () => callback());
    }
    set disabled(v: boolean) {
        if (v) {
            this.button.setAttribute("disabled", "disabled");
        } else {
            this.button.removeAttribute("disabled");
        }
    }
}

interface InputNumberOptions {
    value?: number;
    step?: number;
    min?: number;
    max?: number;
}

export class InputNumber {
    private readonly input: HTMLInputElement;
    constructor(selector: string, options: InputNumberOptions = {}) {
        this.input = document.querySelector<HTMLInputElement>(selector)!;

        if (typeof options.value !== "undefined") {
            this.input.value = String(options.value);
        }
        if (typeof options.step !== "undefined") {
            this.input.setAttribute("step", String(options.step));
        }
        if (typeof options.min !== "undefined") {
            this.input.setAttribute("min", String(options.min));
        }
        if (typeof options.max !== "undefined") {
            this.input.setAttribute("max", String(options.max));
        }
    }
    get value() {
        return Number(this.input.value);
    }
    set value(val: number) {
        this.input.value = String(val);
    }
    onChange(callback: () => void) {
        this.input.addEventListener("change", () => callback());
    }
}

export class InputText {
    private readonly input: HTMLInputElement;
    constructor(selector: string) {
        this.input = document.querySelector<HTMLInputElement>(selector)!;
    }
    set value(v: string) {
        this.input.value = v;
    }
    get value() {
        return this.input.value;
    }
    on(event: string, callback: () => void) {
        if (event === "enter") {
            this.input.addEventListener("keypress", (event) => {
                if (event.key === "Enter") {
                    callback();
                }
            });
        } else {
            this.input.addEventListener(event, () => callback());
        }
    }
}

export class Checkbox {
    private readonly checkbox: HTMLInputElement;
    constructor(selector: string) {
        this.checkbox = document.querySelector<HTMLInputElement>(selector)!;
    }
    set value(v) {
        this.checkbox.checked = v === "1";
    }
    get value() {
        return this.checkbox.checked ? "1" : "0";
    }
    onChange(callback: () => void) {
        this.checkbox.addEventListener("change", () => callback());
    }
}

export interface SelectOption {
    value: string;
    text: string;
}

export class Selectbox {
    private readonly select: HTMLSelectElement;
    constructor(selector: string) {
        this.select = document.querySelector<HTMLSelectElement>(selector)!;
    }
    get value() {
        return this.select.value;
    }
    set value(v) {
        this.select.value = v;
    }
    setOptions(options: SelectOption[]) {
        this.select.textContent = "";
        for (const { value, text } of options) {
            const option = document.createElement("option");
            option.setAttribute("value", value);
            option.innerHTML = text;
            this.select.appendChild(option);
        }
    }
    setGroupedOptions(options: Map<string, { value: string; text: string }[]>) {
        this.select.textContent = "";
        for (const [groupNameJ, optionArray] of options.entries()) {
            const optgroup = document.createElement("optgroup");
            optgroup.setAttribute("label", groupNameJ);
            for (const { value, text } of optionArray) {
                const option = document.createElement("option");
                option.setAttribute("value", value);
                option.innerHTML = text;
                optgroup.appendChild(option);
            }
            this.select.appendChild(optgroup);
        }
    }
    onChange(callback: () => void) {
        this.select.addEventListener("change", () => callback());
    }
}

export class SelectButton {
    private callback: () => void;
    private readonly wrapper: HTMLElement;
    private buttons: HTMLElement[];
    constructor(selector: string) {
        this.callback = () => {};
        this.wrapper = document.querySelector<HTMLElement>(selector)!;
        this.registerEvent();
    }
    registerEvent() {
        this.buttons = Array.from(this.wrapper.children).filter((obj) =>
            obj.classList.contains("select-button__button"),
        ) as HTMLElement[];
        for (const button of this.buttons) {
            button.addEventListener("click", () => {
                if (!button.classList.contains("select-button__button--s")) {
                    this.value = button.dataset.value ?? null;
                    this.callback();
                }
            });
        }
    }
    set value(v) {
        for (const button of this.buttons) {
            if (button.dataset.value === v) {
                button.classList.add("select-button__button--s");
            } else {
                button.classList.remove("select-button__button--s");
            }
        }
    }
    get value() {
        const selectedButtons = this.buttons.filter((button) =>
            button.classList.contains("select-button__button--s"),
        );
        if (selectedButtons.length === 0) {
            return null;
        } else {
            return selectedButtons[0].dataset.value ?? null;
        }
    }
    onChange(callback: () => void) {
        this.callback = callback;
    }
    setButtons(options: SelectOption[]) {
        this.wrapper.innerHTML = "";
        for (const { value, text } of options) {
            const button = document.createElement("span");
            button.dataset.value = value;
            button.innerHTML = text;
            button.classList.add("select-button__button");
            this.wrapper.appendChild(button);
        }
        this.registerEvent();
    }
}

import "@simonwep/pickr/dist/themes/nano.min.css";
// import "../style/pickr.css";
import type Pickr from "@simonwep/pickr";

export interface ColorObject {
    color: string;
    opacity: number;
    hex8: string;
}

export class ColorPicker {
    private picker: Pickr | null = null;
    private pickerPromise: Promise<Pickr> | null = null;
    private changeCallback: () => void = () => {};
    private eventFire = true;
    private color: string;
    private swatches: string[];
    private readonly opacityEnabled: boolean;
    private readonly button: HTMLButtonElement;
    constructor(
        selector: string,
        color: string,
        swatches: string[],
        prop = {},
    ) {
        const { opacity } = Object.assign(
            {
                kind: "none",
                opacity: true,
            },
            prop,
        );
        this.color = this.normalize(color);
        this.swatches = [...swatches];
        this.opacityEnabled = opacity;

        // Pickr本体を作らなくても従来と同じ色ボタンを表示できる軽量な受け皿。
        // 最初の操作時にだけPickrのJS chunkを読み、既存buttonとして利用する。
        const original = document.querySelector<HTMLElement>(selector)!;
        original.style.display = "none";
        const wrapper = document.createElement("span");
        wrapper.className = "pickr lazy-pickr";
        this.button = document.createElement("button");
        this.button.type = "button";
        this.button.className = "pcr-button";
        this.button.setAttribute("aria-label", "色を選択");
        wrapper.appendChild(this.button);
        original.parentNode!.insertBefore(wrapper, original);
        this.updateButton();
        this.button.addEventListener("click", () => {
            if (!this.picker) {
                void this.ensurePicker(true);
            }
        });
    }
    getColor() {
        // PickrのtoHEXA().toString()は不透明色だけalpha末尾を省略する。
        // URL・保存済みパラメータの表現を遅延化前と揃える。
        return this.color.endsWith("FF") ? this.color.slice(0, 7) : this.color;
    }
    setColor(color: string) {
        this.color = this.normalize(color);
        this.updateButton();
        if (!this.picker) {
            return;
        }
        this.eventFire = false;
        this.picker.setColor(this.color);
        this.eventFire = true;
    }
    getHex() {
        return this.color.slice(0, 7);
    }
    getOpacity() {
        return Number.parseInt(this.color.slice(7, 9), 16) / 255;
    }
    getColorObject(): ColorObject {
        return {
            color: this.getHex(),
            opacity: this.getOpacity(),
            hex8: this.getColor(),
        };
    }
    setSwatches(colors: string[]) {
        const previousLength = this.swatches.length;
        this.swatches = [...colors];
        if (!this.picker) {
            return;
        }
        for (let i = 0; i < previousLength; i++) {
            this.picker.removeSwatch(0);
        }
        for (const swatch of colors) {
            this.picker.addSwatch(swatch);
        }
    }
    onChange(callback: () => void) {
        this.changeCallback = callback;
    }
    private ensurePicker(show: boolean) {
        if (!this.pickerPromise) {
            this.pickerPromise = import("@simonwep/pickr").then(
                ({ default: PickrConstructor }) => {
                    const picker = PickrConstructor.create({
                        el: this.button,
                        useAsButton: true,
                        theme: "nano",
                        comparison: false,
                        default: this.color,
                        swatches: this.swatches,
                        components: {
                            preview: true,
                            opacity: this.opacityEnabled,
                            hue: true,
                            interaction: {
                                input: true,
                                hex: true,
                            },
                        },
                    });
                    picker.on("change", () => {
                        const selected = picker.getColor();
                        if (selected) {
                            this.color = this.normalize(
                                selected.toHEXA().toString(),
                            );
                            this.updateButton();
                        }
                        if (this.eventFire) {
                            this.changeCallback();
                        }
                    });
                    this.picker = picker;
                    return picker;
                },
            );
        }
        return this.pickerPromise.then((picker) => {
            if (show) {
                picker.show();
            }
            return picker;
        });
    }
    private normalize(color: string) {
        const value = color.trim().replace(/^#/, "");
        let expanded: string;
        if (value.length === 3 || value.length === 4) {
            expanded = [...value].map((digit) => digit + digit).join("");
        } else {
            expanded = value;
        }
        if (expanded.length === 6) {
            expanded += "FF";
        }
        if (!/^[0-9a-fA-F]{8}$/.test(expanded)) {
            return "#000000FF";
        }
        return `#${expanded.toUpperCase()}`;
    }
    private updateButton() {
        const alpha = Number.parseInt(this.color.slice(7, 9), 16) / 255;
        const red = Number.parseInt(this.color.slice(1, 3), 16);
        const green = Number.parseInt(this.color.slice(3, 5), 16);
        const blue = Number.parseInt(this.color.slice(5, 7), 16);
        this.button.style.setProperty(
            "--pcr-color",
            `rgba(${red}, ${green}, ${blue}, ${alpha})`,
        );
    }
}

import dayjs from "dayjs";
import "flatpickr/dist/flatpickr.min.css";
import flatpickr from "flatpickr";
import { Japanese } from "flatpickr/dist/l10n/ja.js";

export class DateTimePicker {
    private callback: () => void;
    private readonly dtp: flatpickr.Instance;
    constructor(selector: string, options = {}) {
        this.callback = () => {};
        Japanese.firstDayOfWeek = 0;
        this.dtp = flatpickr(
            selector,
            Object.assign(
                {
                    hourIncrement: 1,
                    minuteIncrement: 0,
                },
                options,
                {
                    dateFormat: "Y/m/d H:i",
                    enableTime: true,
                    time_24hr: true,
                    locale: Japanese,
                    onClose: () => this.callback(),
                },
            ),
        ) as flatpickr.Instance;
    }
    set value(v: string) {
        this.dtp.setDate(dayjs(v, "YYYY/MM/DD HH:00").toDate());
    }
    get value() {
        return this.dtp.input.value;
    }
    enable() {
        this.dtp._input.removeAttribute("disabled");
    }
    disable() {
        this.dtp._input.setAttribute("disabled", "disabled");
    }
    onChange(callback: () => void) {
        this.callback = callback;
    }
}

export class OnOffButton {
    private callback: () => void = () => {};
    private readonly details: HTMLElement;
    private readonly offButton: HTMLElement;
    private readonly onButton: HTMLElement;
    constructor(id: string) {
        this.details = document.querySelector(`#details_${id}`)!;
        this.offButton = document.querySelector(`#btn_${id}_off`)!;
        this.onButton = document.querySelector(`#btn_${id}_on`)!;

        this.offButton.addEventListener("click", (event) => {
            event.preventDefault();
            this.off();
            this.callback();
        });
        this.onButton.addEventListener("click", (event) => {
            event.preventDefault();
            this.on();
            this.callback();
        });
    }
    private off() {
        this.details.removeAttribute("open");
        this.offButton.classList.remove("btn_offB");
        this.offButton.classList.add("btn_offA");
        this.onButton.classList.remove("btn_onA");
        this.onButton.classList.add("btn_onB");
    }
    private on() {
        this.details.setAttribute("open", "");
        this.offButton.classList.remove("btn_offA");
        this.offButton.classList.add("btn_offB");
        this.onButton.classList.remove("btn_onB");
        this.onButton.classList.add("btn_onA");
    }
    get value() {
        return this.onButton.classList.contains("btn_onA") ? "1" : "0";
    }
    set value(value: string) {
        if (value === "1") {
            this.on();
        } else {
            this.off();
        }
    }
    onChange(callback: () => void) {
        this.callback = callback;
    }
}

export function setFeatureAvailability(id: string, available: boolean) {
    const details = document.querySelector<HTMLElement>(`#details_${id}`);
    if (!details) {
        return;
    }
    details.classList.toggle("feature-unavailable", !available);
    details.toggleAttribute("inert", !available);
    details.setAttribute("aria-disabled", String(!available));
}
